import { useState } from 'react';
import { Link, Outlet, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { AppMode, IntegrationState } from '@blesk/contracts';
import { apiGet } from '../../shared/api';
import { useHasPermission, useLogout, useSession } from '../../shared/session';
import { visibleGroups } from '../../shared/nav';

/** §4 КП: режим — свойство развёртывания, а не сборки. Показываем то, что ответил сервер. */
const MODE_TITLE: Record<AppMode, string> = {
  EMBEDDED_INTEGRATED: 'встроенный режим',
  EXTERNAL_INTEGRATED: 'внешний режим с интеграцией',
  STANDALONE: 'автономный режим',
};

/** Подсказка бейджа — что режим значит для читателя, а не как он определяется. */
const MODE_HINT: Record<AppMode, string> = {
  EMBEDDED_INTEGRATED: 'Приложение открыто внутри портала Bitrix24: вход и задачи — через портал',
  EXTERNAL_INTEGRATED: 'Приложение работает отдельно от портала, но задачи ставит в Bitrix24',
  STANDALONE: 'Приложение работает без Bitrix24: задачи в портал не ставятся',
};

/**
 * §13, §19 ТЗ. Каркас админки: шапка, навигация по разделам, рабочая область.
 *
 * Разметка рассчитана на настольный компьютер и планшет (§19): на узком экране
 * боковое меню становится горизонтальной лентой, таблицы прокручиваются.
 */
export function AppShell() {
  const session = useSession();
  const router = useRouter();
  const logout = useLogout();
  const groups = visibleGroups(session);
  // Меню занимает 264 px, и в таблицах ровно этой ширины не хватало, чтобы
  // разделы перестали уезжать вбок (замер 18.08.2026). Выбор оставлен человеку:
  // в разделе, где он уже находится, навигация нужна реже, чем данные.
  const [navHidden, setNavHidden] = useState(() => localStorage.getItem('nav-hidden') === '1');
  const toggleNav = () => {
    setNavHidden((hidden) => {
      localStorage.setItem('nav-hidden', hidden ? '0' : '1');
      return !hidden;
    });
  };

  // Уходим на вход при любом исходе запроса: кэш к этому моменту уже забыт,
  // и оставаться в разделах не на чем
  const onLogout = () => {
    logout.mutate(undefined, { onSettled: () => router.history.push('/login') });
  };

  // §15.1: вошедшему из портала (`VIBECODE`) выходить некуда — пароля у него
  // нет, форма входа для него тупик, а повторное открытие виджета всё равно
  // входит заново. Кнопка остаётся только сессиям по паролю: во внешнем режиме
  // и админам, вошедшим формой (QA 22.08.2026, дефект 8).
  const canLogout = session.employee?.source !== 'VIBECODE';

  return (
    <div className="layout">
      <header className="topbar">
        <div className="topbar__brand">
          <button
            type="button"
            className="nav-toggle"
            aria-expanded={!navHidden}
            aria-controls="sidebar"
            title={navHidden ? 'Показать разделы' : 'Скрыть разделы'}
            onClick={toggleNav}
          >
            <span aria-hidden="true">☰</span>
            <span className="visually-hidden">
              {navHidden ? 'Показать разделы' : 'Скрыть разделы'}
            </span>
          </button>
          <span className="brand__mark" aria-hidden="true">
            Б
          </span>
          <b>Контроль качества и обращений</b>
          <span className="badge" title={MODE_HINT[session.mode]}>
            {MODE_TITLE[session.mode]}
          </span>
        </div>
        <div className="topbar__user">
          <AttentionCounter />
          <span className="avatar" aria-hidden="true">
            {initials(session.employee?.fullName)}
          </span>
          <span className="topbar__name">{session.employee?.fullName}</span>
          {canLogout && (
            <button type="button" onClick={onLogout} disabled={logout.isPending}>
              Выйти
            </button>
          )}
        </div>
      </header>

      {/* E10: интеграция разомкнута — события принимаются, команды копятся в очереди */}
      {session.mode !== 'STANDALONE' && !session.b24Available && (
        <div className="banner" role="status">
          Интеграция с Bitrix24 недоступна. Опросы и обращения работают, задачи копятся в очереди и
          уйдут после восстановления связи.
        </div>
      )}

      <div className={navHidden ? 'layout__body layout__body--wide' : 'layout__body'}>
        <nav className="sidebar" id="sidebar" aria-label="Разделы" hidden={navHidden}>
          {groups.map(({ group, sections }) => (
            <div key={group} className="sidebar__group">
              <div className="sidebar__title">{group}</div>
              {sections.map((section) => (
                <Link
                  key={section.path}
                  to={section.path}
                  className="sidebar__link"
                  activeProps={{ className: 'sidebar__link sidebar__link--active' }}
                  activeOptions={{ exact: section.path === '/' }}
                >
                  {section.title}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Инициалы для кружка в шапке: фотографий сотрудников система не хранит (§18).
 * Берётся первая буква слова, а не первый символ: у «(Менеджер) Блеск»
 * в кружке стояло «(Б» (QA 22.08.2026, прогон 3).
 */
function initials(fullName: string | undefined): string {
  if (!fullName) return '—';
  const letters = fullName
    .split(/\s+/)
    .map((part) => part.replace(/[^\p{L}\p{N}]/gu, '').charAt(0).toUpperCase())
    .filter((char) => char !== '');
  return letters.slice(0, 2).join('') || '—';
}

/**
 * E7 Дополнения №1, §17.2 ТЗ. Счётчик «Требует внимания» в шапке.
 *
 * Внешней системы алертов в базовом этапе нет (§12 КП), поэтому единственное
 * место, где застрявшая операция становится видна сама, — шапка админки.
 * Счётчик виден на любом экране и ведёт прямо в журнал.
 *
 * Показывается только тем, кто может с этим что-то сделать: право на чтение
 * журнала есть у сисадмина и руководителя менеджеров. Ошибка запроса счётчик
 * прячет, а не ломает экран: мониторинг не повод потерять рабочую страницу.
 */
function AttentionCounter() {
  const canRead = useHasPermission('integration.read');

  const state = useQuery({
    queryKey: ['integration', 'state'],
    queryFn: () => apiGet<IntegrationState>('/api/integration/state'),
    enabled: canRead,
    refetchInterval: 60_000,
  });

  const count = state.data?.attentionCount ?? 0;
  if (!canRead || count === 0) return null;

  return (
    <Link
      to="/integration"
      className="badge badge--err"
      title="Подтверждённые ошибки и операции, результат которых неизвестен"
    >
      Требует внимания: {count}
    </Link>
  );
}
